import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { StoreLayout } from '@/layouts/StoreLayout';
import { AccountLayout } from '@/layouts/AccountLayout';
import { FeatureGate, RedirectIfAuthed, RequireAuth } from '@/features/auth/guards';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AdminGate } from '@/features/admin/guards';
import { adminChildren } from './adminRoutes';
import { PlatformLayout } from '@/layouts/PlatformLayout';
import { PlatformGate } from '@/features/platform/guards';
import { platformChildren } from './platformRoutes';
import { PageLoader } from '@/components/ui/States';
import { AppsPage } from '@/legacy/features/apps/AppsPage';
import { SellerLayout } from '@/legacy/app/layouts/SellerLayout';
import { B2BLayout } from '@/legacy/app/layouts/B2BLayout';
import { PosLayout } from '@/legacy/app/layouts/PosLayout';
import { WarehouseLayout } from '@/legacy/app/layouts/WarehouseLayout';

/* Route-level code splitting: every page is its own chunk.
   LegacyShell is lazy so Feast layout.css (table/th/td) never loads on MCE admin. */
const LegacyShell = lazy(() => import('@/legacy/LegacyShell').then((m) => ({ default: m.LegacyShell })));
const HomePage = lazy(() => import('@/pages/HomePage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('@/pages/ProductDetailPage'));
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const CartPage = lazy(() => import('@/pages/CartPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const OffersPage = lazy(() => import('@/pages/OffersPage'));
const LoginPage = lazy(() => import('@/pages/AuthPages').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/AuthPages').then((m) => ({ default: m.RegisterPage })));
const AboutPage = lazy(() => import('@/pages/StaticPages').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('@/pages/StaticPages').then((m) => ({ default: m.ContactPage })));
const NotFoundPage = lazy(() => import('@/pages/StaticPages').then((m) => ({ default: m.NotFoundPage })));
const OverviewPage = lazy(() => import('@/pages/account/OverviewPage'));
const OrdersPage = lazy(() => import('@/pages/account/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/account/OrderDetailPage'));
const ProfilePage = lazy(() => import('@/pages/account/ProfilePage'));
const WalletPage = lazy(() => import('@/pages/account/WalletPage'));
const LoyaltyPage = lazy(() => import('@/pages/account/LoyaltyPage'));
const CouponsPage = lazy(() => import('@/pages/account/CouponsPage'));
const ReferralsPage = lazy(() => import('@/pages/account/ReferralsPage'));
const WishlistPage = lazy(() => import('@/pages/account/WishlistPage'));

const SellerDashboard = lazy(() => import('@/legacy/features/seller/SellerDashboard').then((m) => ({ default: m.SellerDashboard })));
const SellerProductsPage = lazy(() => import('@/legacy/features/seller/catalog/SellerProductsPage').then((m) => ({ default: m.SellerProductsPage })));
const SellerOrdersPage = lazy(() => import('@/legacy/features/seller/orders/SellerOrdersPage').then((m) => ({ default: m.SellerOrdersPage })));
const SellerOrderDetail = lazy(() => import('@/legacy/features/seller/orders/SellerOrderDetail').then((m) => ({ default: m.SellerOrderDetail })));
const SellerRmaPage = lazy(() => import('@/legacy/features/seller/orders/SellerRmaPage').then((m) => ({ default: m.SellerRmaPage })));
const SellerEarningsPage = lazy(() => import('@/legacy/features/seller/finance/SellerEarningsPage').then((m) => ({ default: m.SellerEarningsPage })));
const SellerPayoutsPage = lazy(() => import('@/legacy/features/seller/finance/SellerPayoutsPage').then((m) => ({ default: m.SellerPayoutsPage })));
const SellerProfilePage = lazy(() => import('@/legacy/features/seller/profile/SellerProfilePage').then((m) => ({ default: m.SellerProfilePage })));

const B2BDashboard = lazy(() => import('@/legacy/features/b2b/B2BDashboard').then((m) => ({ default: m.B2BDashboard })));
const B2BQuotesPage = lazy(() => import('@/legacy/features/b2b/B2BQuotesPage').then((m) => ({ default: m.B2BQuotesPage })));
const B2BQuoteDetail = lazy(() => import('@/legacy/features/b2b/B2BQuoteDetail').then((m) => ({ default: m.B2BQuoteDetail })));
const B2BRequisitionsPage = lazy(() => import('@/legacy/features/b2b/B2BRequisitionsPage').then((m) => ({ default: m.B2BRequisitionsPage })));
const B2BQuickOrderPage = lazy(() => import('@/legacy/features/b2b/B2BQuickOrderPage').then((m) => ({ default: m.B2BQuickOrderPage })));
const B2BPOsPage = lazy(() => import('@/legacy/features/b2b/B2BPOsPage').then((m) => ({ default: m.B2BPOsPage })));
const B2BCreditPage = lazy(() => import('@/legacy/features/b2b/B2BCreditPage').then((m) => ({ default: m.B2BCreditPage })));
const B2BUsersPage = lazy(() => import('@/legacy/features/b2b/B2BUsersPage').then((m) => ({ default: m.B2BUsersPage })));

const PosTerminal = lazy(() => import('@/legacy/features/pos/PosTerminal').then((m) => ({ default: m.PosTerminal })));
const PosShiftOpen = lazy(() => import('@/legacy/features/pos/PosShiftOpen').then((m) => ({ default: m.PosShiftOpen })));
const PosShiftClose = lazy(() => import('@/legacy/features/pos/PosShiftClose').then((m) => ({ default: m.PosShiftClose })));
const PosHistory = lazy(() => import('@/legacy/features/pos/PosHistory').then((m) => ({ default: m.PosHistory })));
const PosOfflineQueue = lazy(() => import('@/legacy/features/pos/PosOfflineQueue').then((m) => ({ default: m.PosOfflineQueue })));

const WarehouseDashboard = lazy(() => import('@/legacy/features/warehouse/WarehouseDashboard').then((m) => ({ default: m.WarehouseDashboard })));
const WmsHubPage = lazy(() => import('@/legacy/features/warehouse/WmsHubPage').then((m) => ({ default: m.WmsHubPage })));
const PickPackPage = lazy(() => import('@/legacy/features/warehouse/PickPackPage').then((m) => ({ default: m.PickPackPage })));
const TransfersPage = lazy(() => import('@/legacy/features/warehouse/TransfersPage').then((m) => ({ default: m.TransfersPage })));
const StockCountPage = lazy(() => import('@/legacy/features/warehouse/StockCountPage').then((m) => ({ default: m.StockCountPage })));

const gated = (feature: Parameters<typeof FeatureGate>[0]['feature'], el: JSX.Element) => <FeatureGate feature={feature}>{el}</FeatureGate>;

export const router = createBrowserRouter([
  { path: 'admin', element: <AdminGate><AdminLayout /></AdminGate>, children: adminChildren },
  { path: 'super-admin', element: <PlatformGate><PlatformLayout /></PlatformGate>, children: platformChildren },

  { path: 'store/*', element: <Navigate to="/" replace /> },
  { path: 'merchant/*', element: <Navigate to="/admin" replace /> },
  { path: 'platform/*', element: <Navigate to="/super-admin" replace /> },

  {
    element: (
      <Suspense fallback={<PageLoader />}>
        <LegacyShell />
      </Suspense>
    ),
    children: [
      { path: 'apps', element: <AppsPage /> },
      {
        path: 'seller',
        element: <SellerLayout />,
        children: [
          { index: true, element: <SellerDashboard /> },
          { path: 'products', element: <SellerProductsPage /> },
          { path: 'orders', element: <SellerOrdersPage /> },
          { path: 'orders/:id', element: <SellerOrderDetail /> },
          { path: 'returns', element: <SellerRmaPage /> },
          { path: 'earnings', element: <SellerEarningsPage /> },
          { path: 'payouts', element: <SellerPayoutsPage /> },
          { path: 'profile', element: <SellerProfilePage /> },
        ],
      },
      {
        path: 'b2b',
        element: <B2BLayout />,
        children: [
          { index: true, element: <B2BDashboard /> },
          { path: 'quotes', element: <B2BQuotesPage /> },
          { path: 'quotes/:id', element: <B2BQuoteDetail /> },
          { path: 'requisitions', element: <B2BRequisitionsPage /> },
          { path: 'quick-order', element: <B2BQuickOrderPage /> },
          { path: 'purchase-orders', element: <B2BPOsPage /> },
          { path: 'credit', element: <B2BCreditPage /> },
          { path: 'users', element: <B2BUsersPage /> },
        ],
      },
      {
        path: 'pos',
        element: <PosLayout />,
        children: [
          { index: true, element: <PosTerminal /> },
          { path: 'open-shift', element: <PosShiftOpen /> },
          { path: 'close-shift', element: <PosShiftClose /> },
          { path: 'history', element: <PosHistory /> },
          { path: 'offline-queue', element: <PosOfflineQueue /> },
        ],
      },
      {
        path: 'warehouse',
        element: <WarehouseLayout />,
        children: [
          { index: true, element: <WarehouseDashboard /> },
          { path: 'hub', element: <WmsHubPage /> },
          { path: 'pick-pack', element: <PickPackPage /> },
          { path: 'transfers', element: <TransfersPage /> },
          { path: 'count', element: <StockCountPage /> },
        ],
      },
    ],
  },

  {
    element: <StoreLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'products', element: gated('products', <ProductsPage />) },
      { path: 'products/:slug', element: gated('products', <ProductDetailPage />) },
      { path: 'categories', element: gated('categories', <CategoriesPage />) },
      { path: 'categories/:slug', element: gated('categories', <CategoryPage />) },
      { path: 'search', element: gated('products', <SearchPage />) },
      { path: 'cart', element: gated('cart', <CartPage />) },
      { path: 'checkout', element: gated('cart', <CheckoutPage />) },
      { path: 'offers', element: <OffersPage /> },
      { path: 'login', element: <RedirectIfAuthed><LoginPage /></RedirectIfAuthed> },
      { path: 'register', element: <RedirectIfAuthed><RegisterPage /></RedirectIfAuthed> },
      { path: 'about', element: <AboutPage /> },
      { path: 'contact', element: <ContactPage /> },
      {
        path: 'account',
        element: <RequireAuth />,
        children: [
          {
            element: <AccountLayout />,
            children: [
              { index: true, element: <OverviewPage /> },
              { path: 'orders', element: gated('orders', <OrdersPage />) },
              { path: 'orders/:id', element: gated('orders', <OrderDetailPage />) },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'wallet', element: gated('wallet', <WalletPage />) },
              { path: 'loyalty', element: gated('loyalty', <LoyaltyPage />) },
              { path: 'coupons', element: gated('coupons', <CouponsPage />) },
              { path: 'referrals', element: gated('affiliate', <ReferralsPage />) },
              { path: 'wishlist', element: <WishlistPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
], {
  future: { v7_relativeSplatPath: true, v7_fetcherPersist: true, v7_normalizeFormMethod: true, v7_partialHydration: true, v7_skipActionErrorRevalidation: true },
});
